import React from 'react';
import { capturePH } from '@/instrumentation-client';

// Converts plain text into React nodes with clickable links for URLs and emails.
// - Preserves newlines with <br />
// - Ensures links inherit surrounding text color and wrap nicely
export function renderTextWithLinks(text: string): React.ReactNode {
  if (!text) return null;

  const elements: React.ReactNode[] = [];
  let lastIndex = 0;
  let keyIndex = 0;

  const linkRegex = /((https?:\/\/|www\.)[^\s<]+|[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/gi;

  const pushTextWithBreaks = (segment: string) => {
    if (!segment) return;
    const parts = segment.split(/\n/);
    parts.forEach((part, idx) => {
      if (part) {
        elements.push(<React.Fragment key={`t-${keyIndex++}`}>{part}</React.Fragment>);
      }
      if (idx < parts.length - 1) {
        elements.push(<br key={`br-${keyIndex++}`} />);
      }
    });
  };

  let match: RegExpExecArray | null;
  while ((match = linkRegex.exec(text)) !== null) {
    const start = match.index;
    const raw = match[0];

    // Push text before the match
    pushTextWithBreaks(text.slice(lastIndex, start));

    // Clean leading/trailing punctuation commonly adjacent to links
    const leadingPunctMatch = raw.match(/^[\(\[\{\"'“‘]+/);
    const trailingPunctMatch = raw.match(/[\)\]\}\"'”’.,;:!?]+$/);

    const leadingPunct = leadingPunctMatch ? leadingPunctMatch[0] : '';
    const trailingPunct = trailingPunctMatch ? trailingPunctMatch[0] : '';

    let core = raw.slice(leadingPunct.length, raw.length - trailingPunct.length);

    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(core);
    const href = isEmail
      ? `mailto:${core}`
      : core.startsWith('http')
        ? core
        : `https://${core}`;

    // Push leading punctuation (if any)
    pushTextWithBreaks(leadingPunct);

    elements.push(
      <a
        key={`a-${keyIndex++}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer nofollow"
        className="underline text-theos-500 break-words max-w-full"
        style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
        onClick={() => {
          try {
            if (/loom\.com/i.test(core)) {
              capturePH('chat_branch_link_clicked', {
                provider: 'loom',
                href,
              })
            }
          } catch {}
        }}
      >
        {core}
      </a>
    );

    // Push trailing punctuation (if any)
    pushTextWithBreaks(trailingPunct);

    lastIndex = start + raw.length;
  }

  // Push remaining text after last match
  pushTextWithBreaks(text.slice(lastIndex));

  return <>{elements}</>;
}


